import React, {ReactNode, useEffect, useRef, useState} from "react";

export enum ToastSkin {
    danger = 'danger',
    error = 'error',
    approval = 'approval',
    neutral = 'neutral'
}

export enum ToastType {
    default = 'default',
    notification = 'notification',
}

export interface ShowToastProps {
    title?: string;
    text: string;
    icon?: ReactNode | ReactNode[];
    skin?: ToastSkin;
    displayTime?: number;
    buttons?: any[];
    type?: ToastType;
}

export interface ToastContextProps {
    showToast: (props: ShowToastProps) => void;
    title?: string;
    text: string | ReactNode | ReactNode[];
    icon?: ReactNode | ReactNode[];
    skin: ToastSkin;
    isShown: boolean;
    buttons?: any[];
    closeToast: () => void;
    type?: ToastType;
}

export interface ToastContextProviderProps {
    children: ReactNode | ReactNode[];
}

export const defaultToastContextValue = {
    showToast: (props: ShowToastProps) => null,
    text: '',
    skin: ToastSkin.neutral,
    isShown: false,
    buttons: [],
    closeToast: () => null,
    type: ToastType.default,
}

export const ToastContext = React.createContext<ToastContextProps>(defaultToastContextValue);

export const ToastContextProvider = ({ children }: ToastContextProviderProps) => {
    const [isToastShown, setIsToastShown] = useState(false);
    const [toastData, setToastData] = useState<any>(null);
    const timeoutRef = useRef<any | null>(null);
    const nextToastProps = useRef<any>(null);

    const clearOldTimeouts = () => {
        if(timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }

    const showToast = (props: ShowToastProps) => {
        if(isToastShown) {
            nextToastProps.current = props;
            closeToast();
        } else {
            nextToastProps.current = null;
            clearOldTimeouts();
            setToastData(props);
        }
    }

    const closeToast = () => {
        clearOldTimeouts();
        setIsToastShown(false);
    }

    useEffect(() => {
        if(!isToastShown && nextToastProps) {
            showToast(nextToastProps.current);
        } else if(!isToastShown) {
            setToastData(null);
        }
    }, [isToastShown]);

    useEffect(() => {
        if(toastData?.text && toastData.skin) {
            clearOldTimeouts();
            setIsToastShown(true);
            if(toastData.buttons?.length && !toastData?.displayTime) return;

            timeoutRef.current = setTimeout(() => {
                setIsToastShown(false);
                setTimeout(() => {
                    setToastData(null);
                }, 500);
            }, toastData?.displayTime || 5000);
        }
    }, [toastData]);

    return (
        <ToastContext.Provider value={{
            showToast,
            title: toastData?.title || '',
            text: toastData?.text || '',
            icon: toastData?.icon,
            skin: toastData?.skin || ToastSkin.neutral,
            isShown: isToastShown,
            closeToast: closeToast,
            buttons: toastData?.buttons,
            type: toastData?.type,
        }}>
            {children}
        </ToastContext.Provider>
    );
}
